import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import useMapParams from './useMapParams';
import useAnalyticsState from '../pages/utils/analyticsStateHooks';

// Overview is intentionally local (R6). Warn loudly in dev if someone wires this hook there.
const LOCAL_ONLY_PATHS = ['/overview'];

/**
 * Global single-select commodity, shared by /analytics and /map.
 *
 * Read priority: URL `?commodity=` → Firestore-persisted analytics state → ''.
 * Writes update both URL (for deep-links / shareability) and Firestore (for
 * cross-session persistence) in one call, so the two stay in sync.
 *
 * Returns [commodity, setCommodity]. Empty string = "no selection / show all".
 * Do NOT mount on /overview — Overview is intentionally local (R6).
 */
const useCommodityParam = () => {
  const [searchParams, setSearchParams] = useMapParams();
  const { pathname } = useLocation();
  const { state, updateState } = useAnalyticsState();

  useEffect(() => {
    if (import.meta.env?.DEV && LOCAL_ONLY_PATHS.includes(pathname)) {
      // eslint-disable-next-line no-console
      console.warn(
        '[useCommodityParam] mounted on a local-only route (' +
          pathname +
          '). Overview is intentionally disconnected from the global commodity. ' +
          'Use a local useState there instead.',
      );
    }
  }, [pathname]);

  const urlCommodity = searchParams.get('commodity') || '';
  const persistedCommodity = (state && state.selectedCommodity) || '';
  const commodity = urlCommodity || persistedCommodity;

  // When a deep link supplies a commodity via URL that differs from what
  // Firestore has, persist it once so subsequent navigations without the param
  // (or other tabs) reflect the user's actual intent. Skip when state hasn't
  // resolved yet (avoids racing the Firestore fetch).
  useEffect(() => {
    if (!urlCommodity) return;
    if (state == null) return;
    if (urlCommodity === persistedCommodity) return;
    if (typeof updateState !== 'function') return;
    updateState({ selectedCommodity: urlCommodity });
  }, [urlCommodity, persistedCommodity, state, updateState]);

  const setCommodity = useCallback(
    (next) => {
      const value = next || '';
      const params = new URLSearchParams(searchParams);
      if (value) params.set('commodity', value);
      else params.delete('commodity');
      setSearchParams(params, { replace: true });
      // Persist for cross-session + cross-page reuse. Best-effort; no-op if no email.
      if (typeof updateState === 'function') {
        updateState({ selectedCommodity: value });
      }
    },
    [searchParams, setSearchParams, updateState],
  );

  return [commodity, setCommodity];
};

export default useCommodityParam;
